import { useState, useEffect } from 'react';
import { api } from '../api';
import { styles } from '../styles';

export default function ReviewScreen({ receipt, onDone }) {
  const [categoryId, setCategoryId] = useState(receipt?.category?.id || '');
  const [expenseType, setExpenseType] = useState(receipt?.expense_type || 'personal');
  const [transactionDate, setTransactionDate] = useState(() => {
    if (receipt?.transaction_date) {
      return new Date(receipt.transaction_date).toISOString().split('T')[0];
    }
    return new Date().toISOString().split('T')[0];
  });
  const [categories, setCategories] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Split state
  const [isSplit, setIsSplit] = useState(false);
  const [splits, setSplits] = useState([{ categoryId: '', amount: '' }]);

  useEffect(() => {
    api.get('/categories')
      .then(setCategories)
      .catch(() => {});
  }, []);

  const grandTotal = Number(receipt?.grand_total) || 0;
  const splitsTotal = splits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
  const remainder = grandTotal - splitsTotal;

  const addSplit = () => {
    setSplits([...splits, { categoryId: '', amount: '' }]);
  };

  const removeSplit = (index) => {
    if (splits.length > 1) {
      setSplits(splits.filter((_, i) => i !== index));
    }
  };

  const updateSplit = (index, field, value) => {
    const updated = [...splits];
    updated[index][field] = value;
    setSplits(updated);
  };

  const handleSave = async () => {
    if (!categoryId) {
      setError('Please select a category');
      return;
    }

    if (isSplit) {
      const invalidSplits = splits.some(s => !s.categoryId || !s.amount);
      if (invalidSplits) {
        setError('Please fill in all split fields');
        return;
      }
      if (splitsTotal >= grandTotal) {
        setError('Splits must be less than the total amount');
        return;
      }
      if (remainder <= 0) {
        setError('Nothing left for primary category');
        return;
      }
    }

    const updates = {
      category_id: categoryId,
      expense_type: expenseType,
    };

    const originalDate = receipt?.transaction_date 
      ? new Date(receipt.transaction_date).toISOString().split('T')[0]
      : null;
    if (transactionDate !== originalDate) {
      updates.transaction_date = new Date(transactionDate).toISOString();
    }

    if (isSplit) {
      updates.splits = splits.map(s => ({
        category_id: s.categoryId,
        amount: parseFloat(s.amount),
      }));
    }

    setSaving(true);
    setError(null);

    try {
      await api.patch(`/receipts/${receipt.id}`, updates);
      onDone();
    } catch (e) {
      setError(e.message);
      setSaving(false);
    }
  };

  const selectedCategory = categories.find(c => c.id === categoryId);

  if (!receipt) return null;

  return (
    <div style={styles.screen}>
      <header style={styles.header}>
        <div style={{ width: 20 }} />
        <h1 style={styles.headerTitle}>Review</h1>
        <div style={{ width: 20 }} />
      </header>

      <div style={styles.receiptCard}>
        <div style={styles.receiptMerchant}>
          {receipt.merchant_name || 'Unknown Merchant'}
        </div>
        <div style={styles.receiptAmount}>
          ${grandTotal.toFixed(2)}
        </div>
      </div>

      <div style={styles.form}>
        <div style={styles.field}>
          <label style={styles.label}>Date</label>
          <input
            style={styles.input}
            type="date"
            value={transactionDate}
            onChange={(e) => setTransactionDate(e.target.value)}
          />
          {!receipt.transaction_date && (
            <p style={styles.hint}>Date not detected — please verify</p>
          )}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Category</label>
          <select
            style={styles.select}
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            <option value="">Select category</option>
            {categories.filter(c => c.is_active && c.parent_id === null && c.budget_type !== 'project').map(c => (
              <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
            ))}
            {categories.filter(c => c.is_active && c.parent_id === null && c.budget_type === 'project').map(proj => {
              const subs = categories.filter(c => c.parent_id === proj.id && c.is_active);
              if (!subs.length) return null;
              return (
                <optgroup key={proj.id} label={`${proj.icon || ''} ${proj.name}`}>
                  {subs.map(c => (
                    <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                  ))}
                </optgroup>
              );
            })}
          </select>
          {receipt.needs_review && (
            <p style={styles.hint}>Low confidence — please verify the category</p>
          )}
        </div>

        <div style={styles.field}>
          <label style={styles.label}>Type</label>
          <div style={styles.segmentedControl}>
            <button
              style={{
                ...styles.segment,
                ...(expenseType === 'personal' ? styles.segmentActive : {})
              }}
              onClick={() => setExpenseType('personal')}
            >
              Personal
            </button>
            <button
              style={{
                ...styles.segment,
                ...(expenseType === 'corporate' ? styles.segmentActive : {})
              }}
              onClick={() => setExpenseType('corporate')}
            >
              Corporate
            </button>
          </div>
        </div>

        {/* Split toggle */}
        <div style={{ ...styles.field, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <input
            type="checkbox"
            id="splitToggle"
            checked={isSplit}
            onChange={(e) => setIsSplit(e.target.checked)}
          />
          <label htmlFor="splitToggle" style={{ ...styles.label, marginBottom: 0 }}>
            Split across categories
          </label>
        </div>

        {isSplit && (
          <div style={styles.field}>
            <label style={styles.label}>Split portions</label>
            
            {splits.map((split, idx) => (
              <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <select
                  style={{ ...styles.input, flex: 2 }}
                  value={split.categoryId}
                  onChange={(e) => updateSplit(idx, 'categoryId', e.target.value)}
                >
                  <option value="">Category...</option>
                  {categories.filter(c => c.is_active && c.id !== categoryId).map((c) => (
                    <option key={c.id} value={c.id}>{c.icon} {c.name}</option>
                  ))}
                </select>
                <input
                  style={{ ...styles.input, flex: 1 }}
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={split.amount}
                  onChange={(e) => updateSplit(idx, 'amount', e.target.value)}
                />
                {splits.length > 1 && (
                  <button
                    style={{ ...styles.backBtn, color: '#ef4444' }}
                    onClick={() => removeSplit(idx)}
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
            
            <button
              style={{ ...styles.secondaryBtn, marginTop: 4 }}
              onClick={addSplit}
            >
              + Add another split
            </button>

            {selectedCategory && (
              <div style={{ 
                marginTop: 12, 
                padding: 12, 
                backgroundColor: '#f0fdf4', 
                borderRadius: 8,
                border: '1px solid #bbf7d0'
              }}>
                <span style={{ color: '#166534', fontSize: 14 }}>
                  {selectedCategory.icon} {selectedCategory.name}: <strong>${remainder.toFixed(2)}</strong> (remainder)
                </span>
              </div>
            )}
          </div>
        )}

        {error && <p style={styles.error}>{error}</p>}

        <button 
          style={styles.primaryBtn} 
          onClick={handleSave} 
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Confirm'}
        </button>
      </div>
    </div>
  );
}